// Projects.jsx - Vault reskin (presentation only). All handlers/state/props preserved.
import React, { useState } from 'react';
import { ArrowLeft, Plus, Search, X, MoreVertical, Trash2, Edit2, Folder } from 'lucide-react';
import '../../styles/vault-modals.css';
import './Projects.css';

const Projects = ({
  projects = [],
  conversations = [],
  onClose,
  onCreate,
  onUpdate,
  onDelete
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('activity');
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showContextMenu, setShowContextMenu] = useState(null);

  // Filter and sort projects
  const filteredProjects = projects
    .filter(p =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'activity') {
        return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
      } else if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });

  // Get conversation count for each project
  const getProjectConversationCount = (projectId) => {
    return conversations.filter(c => c.projectId === projectId).length;
  };

  const handleCreateProject = (projectData) => {
    if (onCreate) {
      onCreate(projectData);
    }
    setShowNewProjectModal(false);
  };

  const handleEditProject = (project) => {
    setSelectedProject(project);
    setShowContextMenu(null);
  };

  const handleDeleteProject = (projectId) => {
    if (window.confirm('Are you sure you want to delete this project? Conversations will not be deleted.')) {
      if (onDelete) {
        onDelete(projectId);
      }
    }
    setShowContextMenu(null);
  };

  return (
    <div className="vault-projects">
      {/* Header */}
      <div className="vp-head">
        <button
          className="vault-btn vault-btn--ghost"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onClose) {
              onClose();
            }
          }}
          type="button"
        >
          <ArrowLeft />
          Back to Chat
        </button>

        <div className="vp-title">
          <span className="vault-modal-sub">QXAI · VAULT // PROJECTS</span>
          <h1>Projects</h1>
        </div>

        <button
          className="vault-btn vault-btn--primary"
          onClick={() => setShowNewProjectModal(true)}
        >
          <Plus />
          New project
        </button>
      </div>

      {/* Search and Sort */}
      <div className="vp-body">
        <div className="vp-toolbar">
          <div className="vp-search">
            <Search />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="vault-field"
            />
          </div>

          <div className="vp-sort">
            <span className="vault-label" style={{ marginBottom: 0 }}>Sort by</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="vault-select-input"
            >
              <option value="activity">Most recent</option>
              <option value="name">Name</option>
            </select>
          </div>
        </div>

        {/* Projects Grid */}
        <div className="vp-grid-wrap">
          {filteredProjects.length === 0 ? (
            <div className="vp-empty">
              <Folder className="vp-empty-icon" />
              <p>
                {searchTerm
                  ? 'No projects found matching your search.'
                  : 'No projects yet. Create your first project to get started.'}
              </p>
              {!searchTerm && (
                <button
                  className="vault-btn vault-btn--primary"
                  onClick={() => setShowNewProjectModal(true)}
                >
                  <Plus />
                  Create your first project
                </button>
              )}
            </div>
          ) : (
            <div className="vp-grid">
              {filteredProjects.map(project => (
                <div key={project.id} className="vault-card vp-card">
                  <div className="vp-card-head">
                    <h3 className="vp-card-title">{project.name}</h3>
                    <div className="vp-card-menu">
                      <button
                        className="vault-icon-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowContextMenu(showContextMenu === project.id ? null : project.id);
                        }}
                        aria-label="Project actions"
                      >
                        <MoreVertical />
                      </button>

                      {showContextMenu === project.id && (
                        <div className="vault-menu vp-context-menu">
                          <button
                            onClick={() => handleEditProject(project)}
                            className="vault-menu-item"
                          >
                            <Edit2 />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteProject(project.id)}
                            className="vault-menu-item is-danger"
                          >
                            <Trash2 />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {project.description && (
                    <p className="vp-card-desc">{project.description}</p>
                  )}

                  <div className="vp-card-foot">
                    <span className="vp-card-count">
                      {getProjectConversationCount(project.id)} conversations
                    </span>
                    <span className="vp-card-date">{formatDate(project.updatedAt || project.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* New Project Modal */}
      {showNewProjectModal && (
        <NewProjectModal
          onClose={() => setShowNewProjectModal(false)}
          onCreate={handleCreateProject}
        />
      )}

      {/* Edit Project Modal */}
      {selectedProject && (
        <EditProjectModal
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
          onUpdate={(updates) => {
            if (onUpdate) {
              onUpdate(updates.id, updates);
            }
            setSelectedProject(null);
          }}
        />
      )}
    </div>
  );
};

// Helper function
function formatDate(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  return date.toLocaleDateString();
}

// New Project Modal Component
const NewProjectModal = ({ onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim()) {
      onCreate({
        id: Date.now().toString(),
        name: name.trim(),
        description: description.trim(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
  };

  return (
    <div className="vault-overlay" onClick={onClose}>
      <div className="vault-modal vault-modal--sm" onClick={(e) => e.stopPropagation()}>
        <div className="vault-modal-head">
          <h2 className="vault-modal-title">Create New Project</h2>
          <div className="spacer" />
          <button className="vault-icon-btn" onClick={onClose} aria-label="Close">
            <X />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="vault-modal-body">
            <div style={{ marginBottom: 20 }}>
              <label htmlFor="project-name" className="vault-label">Project Name *</label>
              <input
                id="project-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter project name..."
                autoFocus
                required
                className="vault-field"
              />
            </div>

            <div>
              <label htmlFor="project-description" className="vault-label">Description</label>
              <textarea
                id="project-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter project description (optional)..."
                rows={4}
                className="vault-textarea"
              />
            </div>
          </div>

          <div className="vault-modal-foot">
            <button type="button" onClick={onClose} className="vault-btn vault-btn--ghost">
              Cancel
            </button>
            <button type="submit" disabled={!name.trim()} className="vault-btn vault-btn--primary">
              Create Project
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Project Modal Component
const EditProjectModal = ({ project, onClose, onUpdate }) => {
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim()) {
      onUpdate({
        ...project,
        name: name.trim(),
        description: description.trim(),
        updatedAt: new Date().toISOString()
      });
    }
  };

  return (
    <div className="vault-overlay" onClick={onClose}>
      <div className="vault-modal vault-modal--sm" onClick={(e) => e.stopPropagation()}>
        <div className="vault-modal-head">
          <h2 className="vault-modal-title">Edit Project</h2>
          <div className="spacer" />
          <button className="vault-icon-btn" onClick={onClose} aria-label="Close">
            <X />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="vault-modal-body">
            <div style={{ marginBottom: 20 }}>
              <label htmlFor="edit-project-name" className="vault-label">Project Name *</label>
              <input
                id="edit-project-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter project name..."
                autoFocus
                required
                className="vault-field"
              />
            </div>

            <div>
              <label htmlFor="edit-project-description" className="vault-label">Description</label>
              <textarea
                id="edit-project-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter project description (optional)..."
                rows={4}
                className="vault-textarea"
              />
            </div>
          </div>

          <div className="vault-modal-foot">
            <button type="button" onClick={onClose} className="vault-btn vault-btn--ghost">
              Cancel
            </button>
            <button type="submit" disabled={!name.trim()} className="vault-btn vault-btn--primary">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Projects;
